import {
  Box,
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { errorColor, successColor, useAuth } from "../../sdk";
import { currencies } from "../../sdk/codeConstant";

/** Rate Shopping Table View */
const competitorAverage = (competitorDetails, currentHotel) => {
  if (!Array.isArray(competitorDetails) || competitorDetails.length === 0)
    return "-";

  const valid = competitorDetails.filter(
    (item) => item?.minPrice !== -2 && item?.minPrice !== 0,
  );

  if (valid.length === 0) return "NA";

  const priceSum = valid.reduce((acc, item) => {
    const price = Number(item?.minPrice);
    return acc + (isNaN(price) ? 0 : price);
  }, 0);

  const avg = Math.round(priceSum / valid.length);
  return `${currencies?.[currentHotel.RSCurrency]?.symbol ?? ""} ${avg}`;
};

const priceValue = (hotel, allCompetitors = []) => {
  const price = allCompetitors.find(
    (item) => String(item?.hotelId) === String(hotel?.rateMetricHotelCode),
  )?.minPrice;

  if (price === -2) {
    return "NA";
  } else if (price === 0) {
    return "Sold-out";
  } else if (price === null || price === undefined || price === null) {
    return "NA";
  } else {
    return Math.round(price);
  }
};

const hotelPrice = (price, currentHotel) => {
  if (price === -2) {
    return "Sold-out";
  } else if (price === 0 || price == null) {
    return "NA";
  } else {
    return `${currencies?.[currentHotel?.RSCurrency]?.symbol} ${price}`;
  }
};

export const PriceCheckerTable = ({
  isSecondaryCompetitor,
  competitors,
  calendarData,
}) => {
  const { currentHotel } = useAuth();
  return (
    <Wrapper>
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          marginY: 3,
          maxHeight: "calc(100vh - 200px)",
          overflow: "auto",
        }}
      >
        <Table fullWidth stickyHeader>
          <TableHead>
            <TableRow>
              <StyledHeaderCell>Date</StyledHeaderCell>
              {currentHotel.isPreciumEnabled && (
                <StyledHeaderCell>Occ %</StyledHeaderCell>
              )}
              <Tooltip title={currentHotel?.name || ""} arrow>
                <StyledHeaderCell>{currentHotel?.name || ""}</StyledHeaderCell>
              </Tooltip>

              <StyledHeaderCell>Competitor Avg.</StyledHeaderCell>

              {competitors
                .filter(
                  (item) =>
                    item.isSecondaryCompetitor === isSecondaryCompetitor,
                )
                ?.map((item, index) => (
                  <Tooltip
                    key={`${item?.id}-${index}`}
                    title={item?.displayKey || ""}
                    arrow
                  >
                    <StyledHeaderCell>
                      {item?.displayKey || ""}
                    </StyledHeaderCell>
                  </Tooltip>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {calendarData.map((hotel, index) => {
              if (!hotel) return "";
              const isSold =
                hotel?.hotelResult?.sourceSortedArray?.[0]?.isSoldOut;
              const hotelMinPrice = hotel?.hotelResult?.minPrice;
              const compAvg = competitorAverage(
                hotel?.competitorsResults,
                currentHotel,
              );
              const sourceURL =
                hotel?.hotelResult?.sourceSortedArray?.[0]?.sourceURL;

              const getCompAvgColor = () => {
                if (compAvg === "NA" || compAvg === "-" || !hotelMinPrice)
                  return "inherit";
                const avgValue = parseFloat(compAvg.replace(/[^\d.]/g, ""));
                return avgValue > hotelMinPrice ? successColor : errorColor;
              };

              return (
                <StyledTableRow
                  key={`${hotel?.rateDate}-${index}`}
                  $isBlur={hotel.isSearching}
                >
                  <StyledTableCell>
                    {hotel?.rateDate
                      ? format(
                          new Date(hotel?.rateDate),
                          "dd/MM/yyyy EEE",
                        ).toUpperCase()
                      : "-"}
                  </StyledTableCell>

                  {currentHotel.isPreciumEnabled && (
                    <StyledTableCell>
                      {isNaN(hotel?.occupancy)
                        ? "NA"
                        : `${hotel?.occupancy.toFixed(2)}%`}
                    </StyledTableCell>
                  )}

                  <StyledTableCell
                    sx={{
                      color: isSold ? errorColor : "inherit",
                      cursor: sourceURL ? "pointer" : "default",
                      ":hover": {
                        textDecoration: sourceURL ? "underline" : "none",
                      },
                    }}
                    onClick={() =>
                      sourceURL &&
                      window.open(sourceURL, "_blank", "noopener,noreferrer")
                    }
                  >
                    {isSold
                      ? "Sold-out"
                      : hotelPrice(hotelMinPrice, currentHotel)}
                  </StyledTableCell>

                  <StyledTableCell
                    sx={{
                      color: getCompAvgColor(),
                    }}
                  >
                    {compAvg}
                  </StyledTableCell>
                  {competitors
                    .filter(
                      (item) =>
                        item.isSecondaryCompetitor === isSecondaryCompetitor,
                    )
                    ?.map((item, idx) => {
                      const compPrice = priceValue(
                        item,
                        hotel?.competitorsResults,
                      );

                      const competitorResult = hotel?.competitorsResults?.find(
                        (comp) =>
                          String(comp?.hotelId) ===
                          String(item?.rateMetricHotelCode),
                      );

                      const sourceURL =
                        competitorResult?.sourceSortedArray?.[0]?.sourceURL;

                      const cellSx = {
                        cursor: sourceURL ? "pointer" : "default",
                        ":hover": {
                          textDecoration: sourceURL ? "underline" : "none",
                        },
                        ...(compPrice !== "NA" && hotelMinPrice
                          ? {
                              color:
                                compPrice > hotelMinPrice
                                  ? successColor
                                  : errorColor,
                            }
                          : {}),
                      };

                      return (
                        <StyledTableCell
                          key={`${item.id}-${idx}`}
                          sx={cellSx}
                          onClick={() =>
                            sourceURL &&
                            window.open(
                              sourceURL,
                              "_blank",
                              "noopener,noreferrer",
                            )
                          }
                        >
                          {!isNaN(compPrice) &&
                            currencies?.[currentHotel?.RSCurrency]?.symbol}{" "}
                          {compPrice}
                        </StyledTableCell>
                      );
                    })}
                  {hotel.isSearching && (
                    <StyledSearching
                      component="img"
                      src="/assets/loading.svg"
                      alt="refreshing"
                      title="Processing"
                    />
                  )}
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Wrapper>
  );
};

// Styled Components

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  minWidth: theme.spacing(15),
  padding: theme.spacing(1, 1),
  fontWeight: 300,
  fontSize: "14px",
  textAlign: "center",
}));

const StyledHeaderCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: "#306FBC",
  color: "#ffffff",
  fontWeight: 700,
  fontSize: "14px",
  textAlign: "center",
  position: "sticky",
  top: 0,
  zIndex: 100,
  maxWidth: theme.spacing(24),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const StyledTableRow = styled(TableRow)(({ $isBlur }) => ({
  borderBottom: `1px solid #e0e0e0`,
  position: "relative",
  "&:nth-of-type(even)": { backgroundColor: "#f5f5f5" },
  "&:nth-of-type(odd)": { backgroundColor: "#ffffff" },
  ...($isBlur && {
    opacity: 0.5,
    pointerEvents: "none",
  }),
}));

const Wrapper = styled(Stack)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  padding: theme.spacing(0, 2, 0, 8),
}));

const StyledSearching = styled(Box)(() => ({
  height: "30px",
  aspectRatio: "1 / 1",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  animation: "spin 1s infinite linear",

  "@keyframes spin": {
    from: {
      transform: "translate(-50%, -50%) rotate(0deg)",
    },
    to: {
      transform: "translate(-50%, -50%) rotate(360deg)",
    },
  },
}));
